import { PaymentProvider, PlanType } from "@brookbush/database";
import {
	type CancellationSurveyFormSchema,
	type CheckoutSurface,
	type PlanConfig,
	StripePriceBillingScheme,
	UserAction,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { getCookie } from "cookies-next";
import { useRouter } from "next/compat/router";
import posthog from "posthog-js";
import type Stripe from "stripe";
import useSWR, { mutate as mutateGlobal } from "swr";

import useSubscriptionDB from "./useSubscriptionDB";
import {
	getCurrentJoiningPriceOffer,
	joiningPriceCheckoutFields,
} from "~/lib/membership-joining-price";
import fetcher from "~/utils/fetcher.client";

const individualPricingFields = (planConfig: PlanConfig) =>
	planConfig.planType === PlanType.INDIVIDUAL
		? joiningPriceCheckoutFields(getCurrentJoiningPriceOffer())
		: {};

const captureMembershipAction = (
	action: "renew" | "pause" | "cancel" | "change_plan",
	outcome: "success" | "error",
	error?: unknown,
) => {
	const responseMessage =
		error &&
		typeof error === "object" &&
		"response" in error &&
		typeof (error as { response?: { data?: { message?: unknown } } }).response
			?.data?.message === "string"
			? (error as { response: { data: { message: string } } }).response.data
					.message
			: undefined;

	posthog.capture("membership:action_result", {
		action,
		outcome,
		...(responseMessage
			? { error_message: responseMessage }
			: error instanceof Error
				? { error_message: error.message }
				: {}),
	});
};

const useStripeSubscription = () => {
	const { userId } = useAuth();

	const { subscriptionDB } = useSubscriptionDB();

	const router = useRouter();

	const path = router?.asPath.split("?")[0]!;

	const {
		data: subscription,
		isLoading,
		mutate,
		error,
	} = useSWR<Stripe.Subscription>(
		userId &&
			subscriptionDB?.paymentProvider === PaymentProvider.STRIPE &&
			`/stripe/subscriptions`,
	);

	const isActive =
		subscription &&
		(subscription.status === "active" ||
			subscription.status === "past_due" ||
			subscription.status === "trialing");

	const price = subscription?.items?.data?.[0]?.price;

	const planType: PlanType =
		price && price.billing_scheme === StripePriceBillingScheme.PER_UNIT
			? PlanType.INDIVIDUAL
			: PlanType.TEAM;

	const quantity =
		subscription?.items?.data?.[0] && planType === PlanType.TEAM
			? subscription.items.data[0].quantity!
			: undefined;

	const generateInvoice = async (
		planConfig: PlanConfig,
		paymentMethod?: Stripe.PaymentMethod,
	) => {
		const res = (await fetcher("/stripe/subscriptions/generate-invoice", {
			data: {
				planConfig: {
					planType: planConfig.planType,
					priceId: planConfig.priceId,

					promoCodeId: planConfig.promoCodeId,
					trialLength: planConfig.trialLength,

					quantity:
						planConfig.planType === PlanType.TEAM
							? planConfig.quantity
							: undefined,
				} as PlanConfig,

				paymentMethodId: paymentMethod?.id,

				ctaClick: getCookie("ctaClick"),
				path,
				ipAddress: getCookie("ip-address"),
				userAgent: getCookie("user-agent"),
				...individualPricingFields(planConfig),
			},
			headers: {
				"x-posthog-session-id": posthog.get_session_id(),
			},
			method: "POST",
		})) as Stripe.Invoice;

		return res;
	};

	const createStripeSubscription = async ({
		planConfig,
		paymentMethod,
		surface,
	}: {
		planConfig: PlanConfig;
		paymentMethod?: Stripe.PaymentMethod;
		surface?: CheckoutSurface;
	}) => {
		const subscription = (await fetcher("/stripe/subscriptions", {
			data: {
				planConfig: {
					planType: planConfig.planType,
					priceId: planConfig.priceId,

					promoCodeId: planConfig.promoCodeId,
					trialLength: planConfig.trialLength,

					quantity:
						planConfig.planType === PlanType.TEAM
							? planConfig.quantity
							: undefined,

					currency: planConfig.currency,
				} as PlanConfig,

				paymentMethodId: paymentMethod?.id,

				referrerId: getCookie("referrer-id"),

				ctaClick: getCookie(`${UserAction.BECOME_A_MEMBER}:cta-click`),
				surface,
				path,
				ipAddress: getCookie("ip-address"),
				userAgent: getCookie("user-agent"),
				...individualPricingFields(planConfig),
			},

			headers: {
				"x-posthog-session-id": posthog.get_session_id(),
			},

			method: "POST",
		})) as Stripe.Subscription;

		mutate(subscription, {
			revalidate: true,
			populateCache: false,
		});

		return subscription;
	};

	const changeStripeSubscription = async (planConfig: PlanConfig) => {
		try {
			const updated = (await fetcher(`/stripe/subscriptions`, {
				method: `PUT`,
				data: {
					promoCodeId: planConfig.promoCodeId,
					priceId: planConfig.priceId,
					quantity:
						planConfig.planType === PlanType.TEAM
							? planConfig.quantity
							: undefined,
					planType: planConfig.planType,
				},

				headers: {
					"x-posthog-session-id": posthog.get_session_id(),
				},
			})) as Stripe.Subscription;

			await mutate(updated, {
				revalidate: true,
				populateCache: false,
			});

			mutateGlobal(`/subscription`);
			captureMembershipAction("change_plan", "success");
			return updated;
		} catch (error) {
			captureMembershipAction("change_plan", "error", error);
			throw error;
		}
	};

	const cancelStripeSubscription = async (
		surveyForm: CancellationSurveyFormSchema,
	) => {
		try {
			await mutate(
				fetcher(`/stripe/subscriptions`, {
					method: `DELETE`,
					data: { surveyForm },
					headers: {
						"x-posthog-session-id": posthog.get_session_id(),
					},
				}),
				{
					revalidate: true,
					populateCache: false,
				},
			);

			mutateGlobal(`/subscription`);
			captureMembershipAction("cancel", "success");
		} catch (error) {
			captureMembershipAction("cancel", "error", error);
			throw error;
		}
	};

	const renewStripeSubscription = async () => {
		try {
			await mutate(
				fetcher(`/stripe/subscriptions`, {
					method: `PATCH`,
					headers: {
						"x-posthog-session-id": posthog.get_session_id(),
					},
				}),
				{
					revalidate: true,
					populateCache: false,
				},
			);

			mutateGlobal(`/subscription`);
			captureMembershipAction("renew", "success");
		} catch (error) {
			captureMembershipAction("renew", "error", error);
			throw error;
		}
	};

	const pauseStripeSubscription = async () => {
		try {
			await mutate(
				fetcher(`/stripe/subscriptions/pause`, {
					method: `PATCH`,
					headers: {
						"x-posthog-session-id": posthog.get_session_id(),
					},
				}),
				{
					revalidate: true,
					populateCache: false,
				},
			);

			mutateGlobal(`/subscription`);
			captureMembershipAction("pause", "success");
		} catch (error) {
			captureMembershipAction("pause", "error", error);
			throw error;
		}
	};

	const addTrialDaysStripeSubscription = async (trialDays: number) => {
		await mutate(
			fetcher(`/stripe/subscriptions/add-trial`, {
				method: `PATCH`,
				headers: {
					"x-posthog-session-id": posthog.get_session_id(),
				},
				data: { trialDays },
			}),
		);
	};

	const endTrial = async () => {
		await mutate(
			fetcher(`/stripe/subscriptions/end-trial`, {
				method: `PATCH`,
				headers: {
					"x-posthog-session-id": posthog.get_session_id(),
				},
			}),
		);
	};

	const isPaused =
		subscription?.pause_collection?.behavior === "void" &&
		subscription?.status !== "canceled";

	return {
		subscription,
		isLoading,
		isActive,
		isPaused,
		price,
		planType,
		quantity,
		endTrial,
		error,

		stripeSubscription: subscription,

		generateInvoice,
		createStripeSubscription,
		changeStripeSubscription,
		cancelStripeSubscription,
		renewStripeSubscription,
		addTrialDaysStripeSubscription,
		pauseStripeSubscription,

		isLoadingStripeSubscription: isLoading,
		refresh: mutate,
	};
};

export default useStripeSubscription;
